import RAPIER from "@dimforge/rapier3d-compat";
import * as THREE from "three";
import type { World as SceneWorld } from "./scene";
import { fractureCup, type Fragment } from "./fracture";
import {
  attachCupColliders,
  CUP_CENTER_Y,
  detachCupColliders,
} from "./physics-cup";
import { buildShelf } from "./physics-shelf";
import { createShardBody, FRACTURE_VELOCITY } from "./physics-fracture";

// Re-exports so the rest of src/ can refer to physics objects without
// importing the Rapier SDK directly. If we ever swap the engine, this
// is the seam that gets re-implemented.
export type PhysicsBody = RAPIER.RigidBody;
export type DragJoint = RAPIER.ImpulseJoint;

/** Async one-time SDK init. Rapier ships its solver as WASM and the
 * RAPIER.* surface isn't usable until this resolves. */
export async function initPhysics(): Promise<void> {
  await RAPIER.init();
}

export interface PhysicsHandle {
  /** Raw Rapier world. Kept on the surface for the dev console + harness
   * scripts only — production code in `src/` should use the wrapper
   * methods on this handle. */
  world: RAPIER.World;
  bodyForGroup: (g: THREE.Group) => PhysicsBody | undefined;
  groupForBody: (b: PhysicsBody) => THREE.Group | undefined;
  addCup: (group: THREE.Group, outerMaterial?: THREE.Material) => PhysicsBody;
  /** Create a kinematic-position-based helper body at the given world
   * position. Used by interaction.ts as the leash for the drag joint —
   * has no collider, only the joint binds it to the dragged cup. */
  createDragHelper: (pos: { x: number; y: number; z: number }) => PhysicsBody;
  /** Update the kinematic helper's target each pointer move; Rapier
   * interpolates to it. */
  setDragHelperPosition: (
    helper: PhysicsBody,
    pos: { x: number; y: number; z: number },
  ) => void;
  /** Spherical joint between a cup and a drag-helper body, anchored at
   * `cupLocalAnchor` (cup-local) and helper origin. Linear position is
   * locked at the anchors; rotation is free, so the cup can swing under
   * the leash. */
  attachDragJoint: (
    cupBody: PhysicsBody,
    helperBody: PhysicsBody,
    cupLocalAnchor: { x: number; y: number; z: number },
  ) => DragJoint;
  /** Detach a drag joint returned by `attachDragJoint`. */
  releaseDragJoint: (joint: DragJoint) => void;
  /** Remove a body from the world (typically the drag helper after a
   * release). The cup bodies registered via `addCup` are managed by
   * `reset()` and shouldn't be removed this way. */
  removeBody: (body: PhysicsBody) => void;
  /** Create a single fracture-shard body with a convex-hull collider.
   * Called by `fracture.ts` via the FractureInput callback so the shard
   * generator doesn't have to know about Rapier directly. */
  createShard: (
    worldCenter: { x: number; y: number; z: number },
    rotation: { x: number; y: number; z: number; w: number },
    hullPoints: Float32Array,
  ) => PhysicsBody | null;
  /**
   * Un-fracture a single cup if it's currently broken. Removes its shards,
   * re-enables the body at its last position, and makes the mesh visible.
   * No-op if the cup wasn't broken. Used by focus mode so a broken cup can
   * still be inspected — it'll be picked up by the focus lerp and flown to
   * the camera target.
   */
  restoreCup: (group: THREE.Group) => void;
  /**
   * Toggle a cup body's "inert" state for focus mode. value=true detaches
   * the cup's colliders and disables the body so it can't be pushed by
   * the (still-present) shelf collider or move under gravity while we
   * lerp it to the camera target. value=false re-attaches colliders and
   * re-enables the body. `Collider.setEnabled(false)` alone doesn't
   * remove a collider from Rapier compat 0.19's broadphase, so the only
   * way to make the cup truly intangible is to remove its colliders.
   */
  setCupInert: (group: THREE.Group, value: boolean) => void;
  /** Rotation a cup had at addCup time. Focus mode lerps toward this so a
   * cup that was knocked over still stands upright while inspected. */
  initRotForGroup: (
    group: THREE.Group,
  ) => { x: number; y: number; z: number; w: number } | undefined;
  /** Cap on each cup's linear-velocity magnitude (m/s) — applied per step
   * so a violent throw can't send a cup to orbit. Default 25; debug HUD
   * exposes a slider. */
  setMaxLinvel: (v: number) => void;
  getMaxLinvel: () => number;
  /**
   * Suppress fracture for one cup. Used while the cup is being dragged: the
   * spherical-joint solver can yank it into the shelf or floor at >8 m/s,
   * which would otherwise trigger the fracture path. Anything the dragged
   * cup hits is still free to break.
   */
  setFractureProtected: (group: THREE.Group, value: boolean) => void;
  /**
   * Register the cat's kinematic body so the fracture-detection path can
   * recognise cat-induced impacts. Cups bumped or knocked off the shelf by
   * the cat should fall and tumble, not shatter — the cat is "soft" by
   * design. Pass null to clear (e.g., on cat unload).
   */
  setCatBodies: (bodies: PhysicsBody[]) => void;
  reset: () => void;
}

interface Registered {
  body: RAPIER.RigidBody;
  group: THREE.Group;
  initPos: { x: number; y: number; z: number };
  initRot: { x: number; y: number; z: number; w: number };
  outerMaterial?: THREE.Material;
  broken: boolean;
  fragments?: Fragment[];
  /** True while the user is dragging this cup — skips the fracture check. */
  fractureProtected: boolean;
}

/**
 * Create the physics world, populate it with environment colliders, and
 * return a handle the rest of the app uses. Caller must `await RAPIER.init()`
 * before calling — Rapier's WASM module is loaded asynchronously and every
 * RAPIER.* type below resolves only after init is done.
 */
export function createPhysics(scene: SceneWorld): PhysicsHandle {
  const world = new RAPIER.World({ x: 0, y: -9.82, z: 0 });
  world.timestep = 1 / 60;

  // Floor: a thin static cuboid sitting just below y=0 so the top face
  // is at the world floor plane. (Rapier doesn't have an infinite plane
  // shape; a wide thin slab is the idiomatic stand-in.)
  const floorBody = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
  world.createCollider(
    RAPIER.ColliderDesc.cuboid(50, 0.05, 50)
      .setTranslation(0, -0.05, 0)
      .setFriction(0.85)
      .setRestitution(0.18),
    floorBody,
  );

  const { body: shelfBody, init: shelfInit } = buildShelf(world, scene);

  const registry: Registered[] = [];
  const byGroup = new WeakMap<THREE.Group, RAPIER.RigidBody>();
  const byHandle = new Map<number, Registered>();
  // Cat body handles, used by the fracture detector to recognise cat→cup
  // contacts and let the cup get pushed off the shelf instead of shattering.
  // The cat is now a multi-body — one kinematic body per major bone-tracked
  // capsule (torso, head, ...) — so we keep a Set rather than a single id.
  const catBodyHandles = new Set<number>();
  // Fractures triggered from the event queue can't mutate the world while
  // we're still draining events (Rapier's broadphase invariants); we queue
  // them and process after step + drain.
  const pendingFractures: Array<{
    reg: Registered;
    impactPoint: THREE.Vector3;
  }> = [];

  // Per-step cup velocity ceiling. The drag-throw spherical joint can yank
  // a cup to absurd speeds; without this, hard throws send cups across the
  // map. 25 is well above the fracture threshold (8) so satisfying throws
  // still break things, but cups stay in the room.
  let maxLinvel = 25;

  function addCup(
    group: THREE.Group,
    outerMaterial?: THREE.Material,
  ): RAPIER.RigidBody {
    // Body sits at the cup's geometric center; group origin is at the base.
    const centerOffsetWorld = new THREE.Vector3(
      0,
      CUP_CENTER_Y,
      0,
    ).applyQuaternion(group.quaternion);
    const cx = group.position.x + centerOffsetWorld.x;
    const cy = group.position.y + centerOffsetWorld.y;
    const cz = group.position.z + centerOffsetWorld.z;

    const body = world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(cx, cy, cz)
        .setRotation({
          x: group.quaternion.x,
          y: group.quaternion.y,
          z: group.quaternion.z,
          w: group.quaternion.w,
        })
        .setLinearDamping(0.12)
        .setAngularDamping(0.2)
        .setCanSleep(true)
        .setCcdEnabled(true), // continuous collision so fast throws don't tunnel
    );

    attachCupColliders(world, body);

    const tr = body.translation();
    const rt = body.rotation();
    const reg: Registered = {
      body,
      group,
      initPos: { x: tr.x, y: tr.y, z: tr.z },
      initRot: { x: rt.x, y: rt.y, z: rt.z, w: rt.w },
      outerMaterial,
      broken: false,
      fractureProtected: false,
    };
    registry.push(reg);
    byGroup.set(group, body);
    byHandle.set(body.handle, reg);

    return body;
  }

  function performFracture(reg: Registered, impactPoint: THREE.Vector3): void {
    if (reg.broken || !reg.outerMaterial) return;
    reg.broken = true;

    reg.group.updateMatrixWorld(true);
    const worldMatrix = reg.group.matrixWorld.clone();
    const linvel = reg.body.linvel();
    const angvel = reg.body.angvel();

    const fragments = fractureCup({
      worldMatrix,
      outerMaterial: reg.outerMaterial,
      addShard: (center, rot, hull) =>
        createShardBody(world, center, rot, hull),
      impactPoint,
    });

    // Detach colliders so the (now invisible) cup body has no presence in
    // the broadphase. Rapier compat 0.19's Collider.setEnabled(false) does
    // NOT remove the collider — other cups would still bounce off the
    // ghost. The body itself stays so reset() can re-enable + re-attach
    // without us tracking every original parameter.
    detachCupColliders(world, reg.body);
    reg.body.setEnabled(false);
    reg.group.visible = false;

    reg.fragments = fragments;
    for (const frag of fragments) {
      scene.cupRoot.add(frag.mesh);
      frag.body.setLinvel({ x: linvel.x, y: linvel.y, z: linvel.z }, true);
      frag.body.setAngvel({ x: angvel.x, y: angvel.y, z: angvel.z }, true);

      const t = frag.body.translation();
      const dx = t.x - impactPoint.x;
      const dy = t.y - impactPoint.y;
      const dz = t.z - impactPoint.z;
      const len = Math.hypot(dx, dy, dz) || 1;
      const impulseMag = 0.06;
      frag.body.applyImpulse(
        {
          x: (dx / len) * impulseMag,
          y: (dy / len) * impulseMag,
          z: (dz / len) * impulseMag,
        },
        true,
      );
    }
  }

  function unfracture(reg: Registered): void {
    if (!reg.broken) return;
    for (const frag of reg.fragments ?? []) {
      scene.cupRoot.remove(frag.mesh);
      world.removeRigidBody(frag.body);
      frag.mesh.geometry.dispose();
    }
    reg.fragments = undefined;
    reg.broken = false;
    reg.group.visible = true;
    reg.body.setEnabled(true);
    attachCupColliders(world, reg.body);
  }

  function reset(): void {
    for (const r of registry) {
      unfracture(r);
      r.body.setTranslation(r.initPos, true);
      r.body.setRotation(r.initRot, true);
      r.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      r.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      r.body.wakeUp();
    }
    // Right the shelf too — it can topple if the cat slams into it,
    // and there's no point resetting the cups onto a tipped-over board.
    shelfBody.setTranslation(shelfInit.pos, true);
    shelfBody.setRotation(shelfInit.rot, true);
    shelfBody.setLinvel({ x: 0, y: 0, z: 0 }, true);
    shelfBody.setAngvel({ x: 0, y: 0, z: 0 }, true);
    shelfBody.wakeUp();
  }

  // Scratch buffers reused every frame — keep at the closure level so
  // we don't allocate per-step.
  const tmpQuat = new THREE.Quaternion();
  const tmpVec = new THREE.Vector3();
  const eventQueue = new RAPIER.EventQueue(true);

  // Per-frame: advance the world in fixed-timestep substeps (Rapier has
  // no built-in substepping), drain collision-start events into the
  // fracture queue, pop one queued fracture (CSG cuts cost ~150-300ms;
  // spreading them across frames avoids a single ~1s hitch on multi-cup
  // pile-ups), then sync every dynamic body back to its three.js group.
  function step(dt: number): void {
    // Use a fixed timestep but allow up to a few catch-up steps if the
    // frame ran long.
    const fixed = 1 / 60;
    const maxSubsteps = 3;
    let remaining = Math.min(dt, fixed * maxSubsteps);
    while (remaining >= fixed * 0.5) {
      world.timestep = fixed;
      world.step(eventQueue);
      remaining -= fixed;

      // Drain collision-start events for fracture detection. We do this
      // inside the loop so each substep's events are honored.
      eventQueue.drainCollisionEvents((handle1, handle2, started) => {
        if (!started) return;
        const c1 = world.getCollider(handle1);
        const c2 = world.getCollider(handle2);
        if (!c1 || !c2) return;
        const b1 = c1.parent();
        const b2 = c2.parent();
        // Both the cat and the shelf are "soft" against cups: contact
        // with either may shove a cup but never shatters it on impact.
        // The shelf carries cups when the cat tips it; without this
        // exemption the cup-on-shelf contact would fire at the shelf's
        // (high) velocity and the entire collection would explode at
        // once. Cups still break on direct floor or cup-cup hits.
        if (
          (b1 && catBodyHandles.has(b1.handle)) ||
          (b2 && catBodyHandles.has(b2.handle))
        ) {
          return;
        }
        if (
          b1?.handle === shelfBody.handle ||
          b2?.handle === shelfBody.handle
        ) {
          return;
        }
        for (const candidate of [b1, b2]) {
          if (!candidate || !candidate.isDynamic()) continue;
          const reg = byHandle.get(candidate.handle);
          if (!reg || reg.broken || !reg.outerMaterial) continue;
          if (reg.fractureProtected) continue;
          const lv = candidate.linvel();
          const speed = Math.hypot(lv.x, lv.y, lv.z);
          if (speed < FRACTURE_VELOCITY) continue;
          const t = candidate.translation();
          pendingFractures.push({
            reg,
            impactPoint: new THREE.Vector3(t.x, t.y, t.z),
          });
          break;
        }
      });
    }

    // CSG-driven fracture stalls the main thread for ~150-300ms per cup.
    // Drain at most one per frame so a multi-cup pile-up spreads the cost
    // across frames instead of stacking into a single ~1s hitch. See
    // docs/fracture-perf.md.
    if (pendingFractures.length > 0) {
      const f = pendingFractures.shift()!;
      performFracture(f.reg, f.impactPoint);
    }

    for (const r of registry) {
      if (r.broken && r.fragments) {
        for (const frag of r.fragments) {
          const t = frag.body.translation();
          const q = frag.body.rotation();
          frag.mesh.position.set(t.x, t.y, t.z);
          frag.mesh.quaternion.set(q.x, q.y, q.z, q.w);
        }
        continue;
      }
      // While the user is dragging this cup, the spherical joint can
      // overpower the floor's contact constraint and pull the cup below
      // y=0. Clamp the body's center to half-height as a backstop so the
      // cup can never tunnel through, regardless of orientation. (Outside
      // drag, normal physics handles floor contact.)
      if (r.fractureProtected) {
        const tr = r.body.translation();
        if (tr.y < CUP_CENTER_Y) {
          r.body.setTranslation({ x: tr.x, y: CUP_CENTER_Y, z: tr.z }, true);
          const v = r.body.linvel();
          if (v.y < 0) r.body.setLinvel({ x: v.x, y: 0, z: v.z }, true);
        }
      }
      // Cap linear velocity. Done after step so it's the post-physics
      // speed that's clamped, including any spike from a hard throw.
      const lv = r.body.linvel();
      const speed2 = lv.x * lv.x + lv.y * lv.y + lv.z * lv.z;
      if (speed2 > maxLinvel * maxLinvel) {
        const k = maxLinvel / Math.sqrt(speed2);
        r.body.setLinvel({ x: lv.x * k, y: lv.y * k, z: lv.z * k }, true);
      }
      const t = r.body.translation();
      const q = r.body.rotation();
      tmpQuat.set(q.x, q.y, q.z, q.w);
      tmpVec.set(0, CUP_CENTER_Y, 0).applyQuaternion(tmpQuat);
      r.group.position.set(t.x - tmpVec.x, t.y - tmpVec.y, t.z - tmpVec.z);
      r.group.quaternion.copy(tmpQuat);
    }

    // Sync the dynamic shelf body back to its visible group. Cups
    // resting on the shelf inherit the shelf's motion automatically via
    // physics — the shelf carries them as it slides or tips.
    {
      const t = shelfBody.translation();
      const q = shelfBody.rotation();
      scene.shelf.position.set(t.x, t.y, t.z);
      scene.shelf.quaternion.set(q.x, q.y, q.z, q.w);
    }
  }

  scene.onUpdate(step);

  return {
    world,
    bodyForGroup: (g) => byGroup.get(g),
    groupForBody: (b) => byHandle.get(b.handle)?.group,
    addCup,
    createDragHelper: (pos) =>
      world.createRigidBody(
        RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
          pos.x,
          pos.y,
          pos.z,
        ),
      ),
    setDragHelperPosition: (helper, pos) => {
      helper.setNextKinematicTranslation(pos);
    },
    attachDragJoint: (cupBody, helperBody, cupLocalAnchor) => {
      const params = RAPIER.JointData.spherical(cupLocalAnchor, {
        x: 0,
        y: 0,
        z: 0,
      });
      return world.createImpulseJoint(params, cupBody, helperBody, true);
    },
    releaseDragJoint: (joint) => {
      world.removeImpulseJoint(joint, true);
    },
    removeBody: (body) => {
      world.removeRigidBody(body);
    },
    createShard: (center, rot, hull) =>
      createShardBody(world, center, rot, hull),
    restoreCup: (group) => {
      const reg = byHandle.get(byGroup.get(group)?.handle ?? -1);
      if (reg) unfracture(reg);
    },
    setCupInert: (group, value) => {
      const reg = byHandle.get(byGroup.get(group)?.handle ?? -1);
      if (!reg) return;
      if (value) {
        reg.body.setLinvel({ x: 0, y: 0, z: 0 }, false);
        reg.body.setAngvel({ x: 0, y: 0, z: 0 }, false);
        detachCupColliders(world, reg.body);
        reg.body.setEnabled(false);
      } else {
        reg.body.setEnabled(true);
        attachCupColliders(world, reg.body);
        reg.body.setLinvel({ x: 0, y: 0, z: 0 }, false);
        reg.body.setAngvel({ x: 0, y: 0, z: 0 }, false);
        reg.body.wakeUp();
      }
    },
    initRotForGroup: (group) => {
      const reg = byHandle.get(byGroup.get(group)?.handle ?? -1);
      return reg?.initRot;
    },
    setFractureProtected: (group, value) => {
      const reg = byHandle.get(byGroup.get(group)?.handle ?? -1);
      if (reg) reg.fractureProtected = value;
    },
    setCatBodies: (bodies) => {
      catBodyHandles.clear();
      for (const b of bodies) catBodyHandles.add(b.handle);
    },
    setMaxLinvel: (v) => {
      maxLinvel = Math.max(0, v);
    },
    getMaxLinvel: () => maxLinvel,
    reset,
  };
}
